import { useEffect } from "react";
import { useForm, useStore } from "@tanstack/react-form";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import styled from "styled-components";
import { FormGroup } from "../../components/FormGroup";
import { FormLabel } from "../../components/FormLabel";
import { Form } from "../../components/Form";
import { Input } from "../../components/Input";
import { SearchableSelect } from "../../components/SearchableSelect";
import { SelectInput } from "../../components/SelectInput";
import { Toggle } from "../../components/Toggle";
import { InfoBox } from "../../components/InfoBox";
import { useCatalogScope } from "../../hooks/useCatalogScope";
import { useGetBookkeepingAccountsQuery } from "../../query/bookkeepingAccountsQuery";
import { useGetCoursesQuery } from "../../query/coursesQuery";
import { useGetModifierGroupsQuery } from "../../query/modifierGroupsQuery";
import { useGetModifiersQuery } from "../../query/modifiersQuery";
import { useGetPrepStationsQuery } from "../../query/prepStationsQuery";
import { useGetUnitsQuery } from "../../query/unitsQuery";
import { spacing, borderRadius } from "@styling/sizing";
import { fontSizes } from "@styling/fonts";
import colors from "@styling/colors";
import { ProductType } from "@KassaroAB/shared/types/enums/ProductType";
import { VariantSelectionMode } from "@KassaroAB/shared/types/enums/VariantSelectionMode";
import type {
  RestCreateProductGroup,
  RestProductGroup,
} from "@KassaroAB/shared/types/productGroup";
import { FormSection } from "../../components/FormSection";
import { CATALOG_NEW_ITEM_ID } from "../catalog/catalogNav";

const FormGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: ${spacing.x2}px;
  align-items: start;
`;

const GridFull = styled.div`
  grid-column: 1 / -1;
`;

const GridCell = styled.div`
  min-width: 0;
`;

const CheckboxList = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing.x1}px;
`;

const CheckboxLabel = styled.label`
  display: flex;
  align-items: center;
  gap: ${spacing.x1}px;
  font-size: 13px;
  cursor: pointer;
`;

const InlineLink = styled(Link)`
  color: ${colors.primary};
  font-weight: 600;
  text-decoration: none;

  &:hover {
    text-decoration: underline;
  }
`;

const EmptyHint = styled.p`
  margin: 0;
  padding: ${spacing.x1}px ${spacing.x2}px;
  font-size: ${fontSizes.small}px;
  line-height: 1.45;
  color: ${colors.textMuted};
  background-color: ${colors.background};
  border: 1px solid ${colors.border};
  border-radius: ${borderRadius.regular}px;
`;

const VAT_OPTIONS = [25, 12, 6, 0] as const;

interface ProductGroupFormProps {
  formId: string;
  group?: RestProductGroup;
  savedTick?: number;
  onSubmit: (data: RestCreateProductGroup) => void;
  onDirtyChange?: (isDirty: boolean) => void;
}

export const ProductGroupForm = ({
  formId,
  group,
  savedTick,
  onSubmit,
  onDirtyChange,
}: ProductGroupFormProps) => {
  const { t } = useTranslation();
  const { scope, catalogPath } = useCatalogScope();
  const { data: units } = useGetUnitsQuery(scope);
  const { data: courses } = useGetCoursesQuery(scope);
  const { data: prepStations } = useGetPrepStationsQuery(scope);
  const { data: modifiersList } = useGetModifiersQuery(scope);
  const { data: modifierGroupsList } = useGetModifierGroupsQuery(scope);
  const { data: bookkeepingAccounts } = useGetBookkeepingAccountsQuery(scope);

  const form = useForm({
    defaultValues: {
      name: group?.name || "",
      VAT: group?.VAT ?? (group ? null : 25),
      type: (group?.type ?? ProductType.PRODUCT) as ProductType | null,
      unitId: group?.unitId ?? null,
      productSyncEnabled: group?.productSyncEnabled ?? false,
      courseId: group?.courseId ?? null,
      prepStationId: group?.prepStationId ?? null,
      includeMainProductAsVariant: group?.includeMainProductAsVariant ?? false,
      variantSelectionMode:
        group?.variantSelectionMode ?? VariantSelectionMode.ALWAYS_PROMPT,
      bookkeepingAccountId: group?.bookkeepingAccountId ?? null,
      takeawayVAT: group?.takeawayVAT ?? null,
      takeawayBookkeepingAccountId: group?.takeawayBookkeepingAccountId ?? null,
      modifierGroups: group?.modifierGroups ?? [],
      modifiers: group?.modifiers ?? [],
      alwaysAppliedModifiers: group?.alwaysAppliedModifiers ?? [],
    },
    onSubmit: ({ value }) => {
      onSubmit({
        name: value.name,
        VAT: value.VAT,
        type: value.type,
        unitId: value.unitId,
        productSyncEnabled: value.productSyncEnabled,
        courseId: value.courseId,
        prepStationId: value.prepStationId,
        includeMainProductAsVariant: value.includeMainProductAsVariant,
        variantSelectionMode: value.variantSelectionMode,
        bookkeepingAccountId: value.bookkeepingAccountId,
        takeawayVAT: value.takeawayVAT,
        takeawayBookkeepingAccountId: value.takeawayBookkeepingAccountId,
        modifierGroups: value.modifierGroups,
        modifiers: value.modifiers,
        alwaysAppliedModifiers: value.alwaysAppliedModifiers,
      });
    },
  });

  const isDirty = useStore(form.store, (state) => !state.isDefaultValue);

  useEffect(() => {
    onDirtyChange?.(isDirty);
    return () => onDirtyChange?.(false);
  }, [isDirty, onDirtyChange]);

  useEffect(() => {
    if (!savedTick) return;
    form.reset(form.state.values);
  }, [savedTick, form]);

  const unitsLoaded = units !== undefined;
  const modifiersLoaded =
    modifiersList !== undefined && modifierGroupsList !== undefined;
  const hasUnits = (units?.length ?? 0) > 0;
  const hasModifiers =
    (modifiersList?.length ?? 0) > 0 || (modifierGroupsList?.length ?? 0) > 0;
  const unitsPath = catalogPath("units", CATALOG_NEW_ITEM_ID);

  return (
    <Form
      id={formId}
      onSubmit={(e) => {
        e.preventDefault();
        e.stopPropagation();
        form.handleSubmit();
      }}
    >
      <InfoBox variant="info">{t("productGroupForm.inheritanceIntro")}</InfoBox>

      <FormSection
        title={t("productForm.sections.basicInformationTitle")}
      >
        <FormGrid>
          <GridFull>
            <form.Field name="name">
              {(field) => (
                <FormGroup>
                  <FormLabel>{t("productForm.name")}</FormLabel>
                  <Input
                    type="text"
                    value={field.state.value}
                    onChange={(e) => field.handleChange(e.target.value)}
                    onBlur={field.handleBlur}
                    required
                  />
                </FormGroup>
              )}
            </form.Field>
          </GridFull>
          <GridCell>
            <form.Field name="VAT">
              {(field) => (
                <FormGroup>
                  <FormLabel>{t("productForm.vat")}</FormLabel>
                  <SelectInput
                    value={field.state.value ?? ""}
                    onChange={(e) =>
                      field.handleChange(
                        e.target.value === "" ? null : Number(e.target.value),
                      )
                    }
                    onBlur={field.handleBlur}
                  >
                    <option value="">{t("productForm.notSet")}</option>
                    {VAT_OPTIONS.map((v) => (
                      <option key={v} value={String(v)}>
                        {v}%
                      </option>
                    ))}
                  </SelectInput>
                </FormGroup>
              )}
            </form.Field>
          </GridCell>
          <GridCell>
            <form.Field name="type">
              {(field) => (
                <FormGroup>
                  <FormLabel>{t("productForm.productType")}</FormLabel>
                  <SelectInput
                    value={field.state.value ?? ""}
                    onChange={(e) =>
                      field.handleChange(
                        e.target.value === ""
                          ? null
                          : (e.target.value as ProductType),
                      )
                    }
                    onBlur={field.handleBlur}
                  >
                    <option value="">{t("productForm.notSet")}</option>
                    <option value={ProductType.PRODUCT}>
                      {t("productForm.typePRODUCT")}
                    </option>
                    <option value={ProductType.SERVICE}>
                      {t("productForm.typeSERVICE")}
                    </option>
                  </SelectInput>
                </FormGroup>
              )}
            </form.Field>
          </GridCell>
          <GridFull>
            {!unitsLoaded || hasUnits ? (
              <form.Field name="unitId">
                {(field) => (
                  <FormGroup>
                    <FormLabel>{t("productForm.unit")}</FormLabel>
                    <SearchableSelect
                      value={field.state.value ?? ""}
                      options={(units ?? []).map((unit) => ({
                        value: unit.id,
                        label: unit.suffix
                          ? `${unit.name} (${unit.suffix})`
                          : unit.name,
                      }))}
                      onChange={(unitId) =>
                        field.handleChange(unitId || null)
                      }
                      onBlur={field.handleBlur}
                      placeholder={t("productForm.unitSearchPlaceholder")}
                      emptyOptionLabel={t("productForm.none")}
                      noResultsLabel={t("common.noMatches")}
                    />
                  </FormGroup>
                )}
              </form.Field>
            ) : (
              <EmptyHint>
                {t("productGroupForm.noUnits")}{" "}
                <InlineLink to={unitsPath}>
                  {t("productGroupForm.createUnit")}
                </InlineLink>
              </EmptyHint>
            )}
          </GridFull>
          <GridFull>
            <form.Field name="productSyncEnabled">
              {(field) => (
                <FormGroup>
                  <FormLabel>
                    {t("productGroupForm.productSyncEnabled")}
                  </FormLabel>
                  <Toggle
                    checked={field.state.value}
                    onChange={field.handleChange}
                    onBlur={field.handleBlur}
                  />
                </FormGroup>
              )}
            </form.Field>
          </GridFull>
        </FormGrid>
      </FormSection>

      <FormSection title={t("productForm.sections.restaurantTitle")}>
        <FormGrid>
          <GridFull>
            <form.Field name="courseId">
              {(field) => (
                <FormGroup>
                  <FormLabel>{t("productForm.course")}</FormLabel>
                  <SelectInput
                    value={field.state.value ?? ""}
                    onChange={(e) =>
                      field.handleChange(e.target.value || null)
                    }
                    onBlur={field.handleBlur}
                  >
                    <option value="">{t("productForm.none")}</option>
                    {courses?.map((c) => (
                      <option key={c.id} value={c.id}>
                        {c.name}
                      </option>
                    ))}
                  </SelectInput>
                </FormGroup>
              )}
            </form.Field>
          </GridFull>
          <GridFull>
            <form.Field name="prepStationId">
              {(field) => (
                <FormGroup>
                  <FormLabel>{t("productForm.prepStation")}</FormLabel>
                  <SelectInput
                    value={field.state.value ?? ""}
                    onChange={(e) =>
                      field.handleChange(e.target.value || null)
                    }
                    onBlur={field.handleBlur}
                  >
                    <option value="">{t("productForm.none")}</option>
                    {prepStations?.map((p) => (
                      <option key={p.id} value={p.id}>
                        {p.name}
                      </option>
                    ))}
                  </SelectInput>
                </FormGroup>
              )}
            </form.Field>
          </GridFull>
          <GridFull>
            <form.Field name="includeMainProductAsVariant">
              {(field) => (
                <FormGroup>
                  <FormLabel>
                    {t("productGroupForm.includeMainProductAsVariant")}
                  </FormLabel>
                  <Toggle
                    checked={field.state.value}
                    onChange={field.handleChange}
                    onBlur={field.handleBlur}
                  />
                </FormGroup>
              )}
            </form.Field>
          </GridFull>
          <GridFull>
            <form.Field name="variantSelectionMode">
              {(field) => (
                <FormGroup>
                  <FormLabel>
                    {t("productGroupForm.variantSelectionMode")}
                  </FormLabel>
                  <SelectInput
                    value={field.state.value}
                    onChange={(e) =>
                      field.handleChange(e.target.value as VariantSelectionMode)
                    }
                    onBlur={field.handleBlur}
                  >
                    <option value={VariantSelectionMode.ALWAYS_PROMPT}>
                      {t("productGroupForm.variantSelectionModeALWAYS_PROMPT")}
                    </option>
                    <option value={VariantSelectionMode.DEFAULT_ON_PRESS}>
                      {t(
                        "productGroupForm.variantSelectionModeDEFAULT_ON_PRESS",
                      )}
                    </option>
                  </SelectInput>
                </FormGroup>
              )}
            </form.Field>
          </GridFull>
        </FormGrid>
      </FormSection>

      <FormSection title={t("productForm.sections.bookkeepingTitle")}>
        <form.Field name="bookkeepingAccountId">
          {(field) => (
            <FormGroup>
              <FormLabel>{t("productForm.bookkeepingAccountId")}</FormLabel>
              <SelectInput
                value={field.state.value ?? ""}
                onChange={(e) => field.handleChange(e.target.value || null)}
                onBlur={field.handleBlur}
              >
                <option value="">{t("productForm.none")}</option>
                {bookkeepingAccounts?.map((a) => (
                  <option key={a.id} value={a.id}>
                    {a.accountNumber} {a.name}
                  </option>
                ))}
              </SelectInput>
            </FormGroup>
          )}
        </form.Field>
      </FormSection>

      <FormSection title={t("productForm.sections.takeawayTitle")}>
        <FormGrid>
          <GridCell>
            <form.Field name="takeawayVAT">
              {(field) => (
                <FormGroup>
                  <FormLabel>{t("productForm.takeawayVat")}</FormLabel>
                  <SelectInput
                    value={field.state.value ?? ""}
                    onChange={(e) =>
                      field.handleChange(
                        e.target.value === "" ? null : Number(e.target.value),
                      )
                    }
                    onBlur={field.handleBlur}
                  >
                    <option value="">{t("productForm.notSet")}</option>
                    {VAT_OPTIONS.map((v) => (
                      <option key={v} value={String(v)}>
                        {v}%
                      </option>
                    ))}
                  </SelectInput>
                </FormGroup>
              )}
            </form.Field>
          </GridCell>
          <GridCell>
            <form.Field name="takeawayBookkeepingAccountId">
              {(field) => (
                <FormGroup>
                  <FormLabel>
                    {t("productForm.takeawayBookkeepingAccount")}
                  </FormLabel>
                  <SelectInput
                    value={field.state.value ?? ""}
                    onChange={(e) =>
                      field.handleChange(e.target.value || null)
                    }
                    onBlur={field.handleBlur}
                  >
                    <option value="">{t("productForm.none")}</option>
                    {bookkeepingAccounts?.map((a) => (
                      <option key={a.id} value={a.id}>
                        {a.accountNumber} {a.name}
                      </option>
                    ))}
                  </SelectInput>
                </FormGroup>
              )}
            </form.Field>
          </GridCell>
        </FormGrid>
      </FormSection>

      <FormSection title={t("productForm.sections.modifiersTitle")}>
        {!modifiersLoaded || hasModifiers ? (
          <>
            <form.Field name="modifierGroups">
              {(field) => (
                <FormGroup>
                  <FormLabel>{t("productGroupForm.modifierGroups")}</FormLabel>
                  <CheckboxList>
                    {modifierGroupsList?.map((mg) => (
                      <CheckboxLabel key={mg.id}>
                        <input
                          type="checkbox"
                          checked={field.state.value.includes(mg.id)}
                          onChange={(e) => {
                            const next = e.target.checked
                              ? [...field.state.value, mg.id]
                              : field.state.value.filter((id) => id !== mg.id);
                            field.handleChange(next);
                          }}
                        />
                        {mg.name}
                      </CheckboxLabel>
                    ))}
                  </CheckboxList>
                </FormGroup>
              )}
            </form.Field>
            <form.Field name="modifiers">
              {(field) => (
                <FormGroup>
                  <FormLabel>{t("productGroupForm.modifiers")}</FormLabel>
                  <CheckboxList>
                    {modifiersList?.map((m) => (
                      <CheckboxLabel key={m.id}>
                        <input
                          type="checkbox"
                          checked={field.state.value.includes(m.id)}
                          onChange={(e) => {
                            const next = e.target.checked
                              ? [...field.state.value, m.id]
                              : field.state.value.filter((id) => id !== m.id);
                            field.handleChange(next);
                          }}
                        />
                        {m.name}
                      </CheckboxLabel>
                    ))}
                  </CheckboxList>
                </FormGroup>
              )}
            </form.Field>
            <form.Field name="alwaysAppliedModifiers">
              {(field) => (
                <FormGroup>
                  <FormLabel>
                    {t("productGroupForm.alwaysAppliedModifiers")}
                  </FormLabel>
                  <CheckboxList>
                    {modifiersList?.map((m) => (
                      <CheckboxLabel key={m.id}>
                        <input
                          type="checkbox"
                          checked={field.state.value.includes(m.id)}
                          onChange={(e) => {
                            const next = e.target.checked
                              ? [...field.state.value, m.id]
                              : field.state.value.filter((id) => id !== m.id);
                            field.handleChange(next);
                          }}
                        />
                        {m.name}
                      </CheckboxLabel>
                    ))}
                  </CheckboxList>
                </FormGroup>
              )}
            </form.Field>
          </>
        ) : (
          <EmptyHint>{t("productGroupForm.noModifiers")}</EmptyHint>
        )}
      </FormSection>
    </Form>
  );
};
